import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/**
 * Fallback swatch colours, keyed by normalised finish name.
 * Overridden by "Finish swatch" blocks in the theme editor.
 */
const DEFAULT_SWATCHES = {
  chrome: '#d9dbde',
  steel: '#a9aaa5',
  stainlesssteel: '#a9aaa5',
  black: '#1f1f1f',
  gunmetal: '#4b4c4f',
  gunmetalgrey: '#4b4c4f',
  gold: '#d6b46f',
  brass: '#b59a5b',
  copper: '#b87350',
  nickel: '#c9c6bf',
  white: '#f4f4f2',
  pewter: '#8e9294',
  antiquebrass: '#8a6e3e',
};

/**
 * Normalises a finish name so look-alike products match across ranges,
 * e.g. "Brushed Steel" === "Steel", "Gun Metal" === "Gunmetal", "Matt Black" === "Black".
 *
 * @param {string | undefined} value
 * @returns {string}
 */
const normaliseFinish = (value) =>
  (value || '')
    .toLowerCase()
    .replace(/\b(brushed|polished|matt|matte|satin|finish)\b/g, '')
    .replace(/[^a-z]/g, '');

/**
 * @typedef {{ src: string, thumb: string, alt: string }} TapImage
 * @typedef {{ id: number, variantId: number, handle: string, title: string, colour: string, price: number,
 *   compareAt: number, available: boolean, discount: number, discountEligible: boolean, addons: number[],
 *   images: TapImage[] }} TapProduct
 * @typedef {{ id: string, handle: string, title: string, description: string, system: string, products: number[],
 *   upgrade: { products: number[], title: string, system: string, text: string } }} TapRange
 */

/**
 * Stepped tap configurator. Adds every selected line in one request, then sends the customer to checkout.
 * Deliberately does not dispatch CartAddEvent, so the cart drawer never opens.
 *
 * @extends {Component}
 */
class TapConfigurator extends Component {
  /** @type {Map<number, TapProduct>} */
  #products = new Map();
  /** @type {TapRange[]} */
  #ranges = [];
  /** @type {any} */
  #data = {};
  #state = {
    rangeId: '',
    baseId: 0,
    upgraded: false,
    boiler: 'standard',
    filterId: '',
    galleryIndex: 0,
    // Kitchen bundle steps
    sinkId: 0,
    sinkTouched: false,
    wasteFinish: '',
    warranty: false,
  };
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {MutationObserver | undefined} */
  #klarnaObserver;
  /** @type {Map<string, string>} product handle -> description HTML */
  #specCache = new Map();
  #summaryVisible = false;
  #sectionVisible = false;

  connectedCallback() {
    super.connectedCallback();

    const dataEl = this.refs.data;
    if (!dataEl) return;

    try {
      this.#data = JSON.parse(dataEl.textContent || '{}');
    } catch (error) {
      console.error('[tap-configurator] Invalid data', error);
      return;
    }

    for (const product of this.#data.products ?? []) this.#products.set(product.id, product);
    this.#ranges = (this.#data.ranges ?? []).filter((range) => this.#rangeProducts(range).length > 0);
    if (!this.#ranges.length) return;

    this.#applyInitialState();
    this.#render();
    this.#observeBar();
    this.#observeKlarna();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#klarnaObserver?.disconnect();
  }

  /* ---------- Derived state ---------- */

  /** @param {TapRange} range */
  #rangeProducts(range) {
    return range.products.map((id) => this.#products.get(id)).filter((product) => product !== undefined);
  }

  get #range() {
    return this.#ranges.find((range) => range.id === this.#state.rangeId) ?? this.#ranges[0];
  }

  get #base() {
    return this.#products.get(this.#state.baseId) ?? this.#firstAvailable(this.#range);
  }

  /** The look-alike 4-in-1 product with the same finish, if the range has one. */
  get #twin() {
    const base = this.#base;
    const key = normaliseFinish(base?.colour);
    if (!key) return undefined;

    return this.#range.upgrade.products
      .map((id) => this.#products.get(id))
      .find((product) => product && normaliseFinish(product.colour) === key);
  }

  get #tap() {
    const twin = this.#twin;
    return this.#state.upgraded && twin?.available ? twin : this.#base;
  }

  get #boilerProduct() {
    return this.#data.boilerId ? this.#products.get(this.#data.boilerId) : undefined;
  }

  get #boilerEligible() {
    const boiler = this.#boilerProduct;
    return Boolean(boiler && this.#tap?.addons.includes(boiler.id));
  }

  get #filter() {
    const option = (this.#data.filters ?? []).find((filter) => filter.id === this.#state.filterId);
    if (!option) return undefined;

    const product = this.#products.get(option.productId);
    return product ? { ...product, label: option.label } : undefined;
  }

  get #lines() {
    const tap = this.#tap;
    /** @type {{ product: TapProduct, name: string, sub: string, focus: string }[]} */
    const lines = [];
    if (!tap) return lines;

    const system = tap === this.#base ? this.#range.system : this.#range.upgrade.system;
    lines.push({
      product: tap,
      // Base tap: the range's display name; 4-in-1 twin: its own range name from the title (e.g. "Verani").
      name: [tap === this.#base ? this.#range.title : this.#rangeName(tap), system].filter(Boolean).join(' '),
      sub: this.#finishLabel(tap),
      focus: 'tap',
    });

    const boiler = this.#boilerProduct;
    if (boiler && this.#boilerEligible && this.#state.boiler === 'space') {
      lines.push({ product: boiler, name: this.#data.boilerLabel || boiler.title, sub: '', focus: 'boiler' });
    }

    const filter = this.#filter;
    if (filter) lines.push({ product: filter, name: filter.label || filter.title, sub: '', focus: 'filter' });

    const sink = this.#sink;
    if (sink) lines.push({ product: sink, name: this.#shortTitle(sink).name, sub: this.#bowlsLabel(sink), focus: 'sink' });

    const waste = this.#waste;
    if (waste) {
      const qty = this.#wasteQty;
      lines.push({
        product: waste,
        name: `${this.#finishLabel(waste)} strainer waste`,
        sub: qty > 1 ? `× ${qty} · ${this.#data.waste?.perBowlLabel || 'One per bowl'}` : '',
        focus: 'waste',
        qty,
      });
    }

    const warranty = this.#warrantyProduct;
    if (warranty && this.#state.warranty) lines.push({ product: warranty, name: warranty.title, sub: '', focus: 'warranty' });

    return lines.map((line) => ({ qty: 1, ...line }));
  }

  get #total() {
    const lines = this.#lines;
    return lines.reduce((sum, line) => sum + line.product.price * line.qty - this.#lineDiscount(line), 0) - this.#orderDiscount(lines);
  }

  /**
   * Percentage page discount, worked out on the whole basket like Shopify's "amount off order" code:
   * eligible subtotal (after any fixed amount off the tap) x %, rounded down to the penny (checked against real checkouts).
   *
   * @param {{ product: TapProduct, qty: number, focus: string }[]} lines
   */
  #orderDiscount(lines) {
    if (!this.#data.discountCode || !(this.#data.discountPercent > 0)) return 0;
    const eligible = lines.reduce(
      (sum, line) => sum + (line.product.discountEligible ? line.product.price * line.qty - this.#lineDiscount(line) : 0),
      0
    );
    return Math.floor((eligible * this.#data.discountPercent) / 100);
  }

  /* ---------- Kitchen bundle: sink, strainer waste, warranty ---------- */

  get #sinks() {
    return (this.#data.sink?.products ?? []).map((id) => this.#products.get(id)).filter((product) => product !== undefined);
  }

  get #sink() {
    return this.#data.sink ? this.#products.get(this.#state.sinkId) : undefined;
  }

  /** @param {TapProduct} sink */
  #sinkBowls(sink) {
    const tags = sink.tags ?? [];
    if (tags.includes('1.5 Bowl')) return '1.5';
    if (tags.includes('2.0 Bowl') || tags.includes('2 Bowl')) return '2';
    return '1';
  }

  /** @param {TapProduct} sink */
  #bowlsLabel(sink) {
    return `${this.#sinkBowls(sink)} bowl`;
  }

  /** A 1.5 or 2 bowl sink has two drains, so it needs two strainer wastes. */
  get #wasteQty() {
    const sink = this.#sink;
    return sink && this.#sinkBowls(sink) !== '1' ? 2 : 1;
  }

  get #sinkHasOverflow() {
    const sink = this.#sink;
    return Boolean(sink && (this.#data.sink?.overflow ?? []).includes(sink.id));
  }

  /** "... | Gold Strainer Waste Supplied | ..." -> "Gold". Null when the title doesn't say. */
  /** @param {TapProduct} sink */
  #suppliedWasteFinish(sink) {
    const match =
      sink.title.match(/([A-Za-z]+(?: [A-Za-z]+)?)\s+(?:basket\s+)?strainer wastes?(?:\(s\))?\s+supplied/i) ||
      sink.title.match(/with\s+([A-Za-z]+(?: [A-Za-z]+)?)\s+(?:basket\s+)?strainer waste/i);
    const finish = match?.[1]?.replace(/^(supplied with|with)\s+/i, '').trim();
    return finish && !/^(and|with|supplied)$/i.test(finish) ? finish.replace(/\b\w/g, (c) => c.toUpperCase()) : null;
  }

  /**
   * Waste upgrades that fit the chosen sink: the overflow-plate version for sinks with an overflow, the plain one
   * otherwise. One option per finish, preferring the Elite range over Eco.
   */
  get #wasteOptions() {
    const overflow = this.#sinkHasOverflow;
    /** @type {Map<string, TapProduct>} */
    const byFinish = new Map();
    for (const id of this.#data.waste?.products ?? []) {
      const product = this.#products.get(id);
      if (!product || /overflow/i.test(product.title) !== overflow) continue;
      const finish = this.#finishLabel(product);
      const current = byFinish.get(finish);
      const better = (p) => (p.available ? 2 : 0) + (/^\s*elite/i.test(p.title) ? 1 : 0);
      if (!current || better(product) > better(current)) byFinish.set(finish, product);
    }
    return [...byFinish.entries()].map(([finish, product]) => ({ finish, product }));
  }

  get #waste() {
    if (!this.#data.waste || !this.#sink || !this.#state.wasteFinish) return undefined;
    return this.#wasteOptions.find((option) => option.finish === this.#state.wasteFinish)?.product;
  }

  get #warrantyProduct() {
    return this.#data.warrantyId ? this.#products.get(this.#data.warrantyId) : undefined;
  }

  /** Does a waste/sink finish match the tap? Exact finish name first ("Brushed Steel"), else the colour family. */
  /** @param {string | null | undefined} finish */
  #matchesTap(finish) {
    const tap = this.#tap;
    if (!tap || !finish) return false;
    const tapFinish = this.#finishLabel(tap).toLowerCase();
    if (finish.toLowerCase() === tapFinish) return true;
    const exactExists = this.#wasteOptions.some((option) => option.finish.toLowerCase() === tapFinish);
    return !exactExists && normaliseFinish(finish) === normaliseFinish(tap.colour || tapFinish);
  }

  /** Sink in the tap's colour family (chrome taps pair with stainless steel), else the cheapest in stock. */
  #defaultSink() {
    const tap = this.#tap;
    let key = normaliseFinish(tap?.colour || (tap ? this.#finishLabel(tap) : ''));
    if (key === 'chrome') key = 'steel';
    const sinks = this.#sinks.filter((sink) => sink.available);
    const match = sinks.find((sink) => key && (sink.tags ?? []).some((tag) => normaliseFinish(tag) === key));
    return match ?? [...sinks].sort((a, b) => a.price - b.price)[0];
  }

  /** "Verossi | Vrimo | 1.5 Bowl ... | Copper Strainer Wastes Supplied | 670 x 440mm | Copper Finish" -> name + spec */
  /** @param {TapProduct} product */
  #shortTitle(product) {
    const parts = product.title
      .split('|')
      .map((part) => part.trim())
      .filter((part, index) => part && !(index === 0 && /^verossi$/i.test(part)))
      .filter((part) => !/waste|left or right|inset or under/i.test(part));
    return { name: parts[0] || product.title, spec: parts.slice(1).join(' · ') };
  }

  /**
   * Fixed page discount on a line, in minor units: applied once per order and only the tap is eligible, so only
   * the tap line carries it. Percentage discounts are order-level on top (see #orderDiscount).
   *
   * @param {{ product: TapProduct, focus: string, qty?: number }} line
   */
  #lineDiscount(line) {
    if (!this.#data.discountCode) return 0;
    return line.focus === 'tap' ? line.product.discount || 0 : 0;
  }

  /** Price after the page discount, for one unit (or `qty` units): fixed amount first, then the percentage. */
  /**
   * @param {TapProduct} product
   * @param {number} [qty]
   */
  #displayPrice(product, qty = 1) {
    if (!this.#data.discountCode) return product.price * qty;
    const price = product.price * qty - (product.discount || 0);
    if (!(this.#data.discountPercent > 0) || !product.discountEligible) return price;
    // Rounded down, as Shopify does, so a single-item order matches checkout to the penny.
    return price - Math.floor((price * this.#data.discountPercent) / 100);
  }

  /** The "was" price to strike through: compare-at when set, otherwise the normal price. */
  /** @param {TapProduct} product */
  #wasPrice(product) {
    return Math.max(product.compareAt || 0, product.price);
  }

  /** @param {TapRange} range */
  #firstAvailable(range) {
    const products = this.#rangeProducts(range);
    return products.find((product) => product.available) ?? products[0];
  }

  /** "Verossi | Vamore | 3 in 1 ... | Polished Chrome Finish" -> "Vamore" */
  /** @param {TapProduct} product */
  #rangeName(product) {
    const parts = product.title.split('|').map((part) => part.trim()).filter(Boolean);
    return parts.length > 2 ? parts[1] : this.#range.title;
  }

  /** "... | Polished Chrome Finish" -> "Polished Chrome" */
  /** @param {TapProduct} product */
  #finishLabel(product) {
    const parts = product.title.split('|').map((part) => part.trim()).filter(Boolean);
    const last = parts.length > 2 ? parts[parts.length - 1].replace(/\s*finish$/i, '') : '';
    return last || product.colour || product.title;
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.#data.moneyFormat || '£{{amount}}', this.#data.currency || 'GBP');
  }

  /* ---------- Initial state (deep links) ---------- */

  #applyInitialState() {
    const params = new URLSearchParams(window.location.search);
    const rangeParam = params.get('range');
    const finishParam = params.get('finish');

    const range = (rangeParam && this.#ranges.find((r) => r.handle === rangeParam)) || this.#ranges[0];
    this.#state.rangeId = range.id;
    this.#state.baseId = this.#firstAvailable(range)?.id ?? 0;

    if (finishParam) {
      const inRange = this.#rangeProducts(range).find((product) => product.handle === finishParam);
      const inUpgrade = range.upgrade.products
        .map((id) => this.#products.get(id))
        .find((product) => product?.handle === finishParam);

      if (inRange) {
        this.#state.baseId = inRange.id;
      } else if (inUpgrade) {
        // Linked straight to the 4-in-1: select its 3-in-1 look-alike and switch the upgrade on.
        const key = normaliseFinish(inUpgrade.colour);
        const twin = this.#rangeProducts(range).find((product) => normaliseFinish(product.colour) === key);
        if (twin) {
          this.#state.baseId = twin.id;
          this.#state.upgraded = true;
        }
      }
    }

    if (params.get('upgrade') === '1') this.#state.upgraded = true;

    const sinkParam = params.get('sink');
    const linkedSink = sinkParam && this.#sinks.find((sink) => sink.handle === sinkParam);
    if (linkedSink) {
      this.#state.sinkId = linkedSink.id;
      this.#state.sinkTouched = true;
    }
  }

  /* ---------- Event handlers (wired via on:* attributes) ---------- */

  openRanges() {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.rangeDialog);
    dialog?.showModal();
    dialog?.querySelector('[aria-current="true"]')?.focus();
  }

  closeRanges() {
    /** @type {HTMLDialogElement | undefined} */ (this.refs.rangeDialog)?.close();
  }

  /** Closes the range dialog when the backdrop is clicked. */
  /** @param {Event} event */
  onDialogClick(event) {
    if (event.composedPath()[0] === this.refs.rangeDialog) this.closeRanges();
  }

  /** @param {Event} event */
  selectRange(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const range = this.#ranges.find((r) => r.id === target.dataset.rangeId);
    this.closeRanges();
    if (!range || range.id === this.#state.rangeId) return;

    // Keep the customer's finish when the new range has it.
    const key = normaliseFinish(this.#base?.colour);
    const sameFinish = this.#rangeProducts(range).find(
      (product) => product.available && key && normaliseFinish(product.colour) === key
    );

    this.#state.rangeId = range.id;
    this.#state.baseId = (sameFinish ?? this.#firstAvailable(range))?.id ?? 0;
    this.#state.upgraded = false;
    this.#state.galleryIndex = 0;
    this.#render();
  }

  /** @param {Event} event */
  selectFinish(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const id = Number(target.dataset.productId);
    if (!id || id === this.#state.baseId) return;

    this.#state.baseId = id;
    this.#state.galleryIndex = 0;
    this.#render();
  }

  /** @param {Event} event */
  setUpgrade(event) {
    this.#state.upgraded = /** @type {HTMLInputElement} */ (event.target).value === '1';
    this.#state.galleryIndex = 0;
    this.#render();
  }

  /** @param {Event} event */
  setBoiler(event) {
    this.#state.boiler = /** @type {HTMLInputElement} */ (event.target).value;
    this.#render('boiler');
  }

  /** @param {Event} event */
  setFilter(event) {
    this.#state.filterId = /** @type {HTMLInputElement} */ (event.target).value;
    this.#render('filter');
  }

  openSinks() {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.sinkDialog);
    dialog?.showModal();
    // Always open at the top of the grid; keep keyboard focus on the current choice without scrolling to it.
    dialog?.scrollTo(0, 0);
    /** @type {HTMLElement | null | undefined} */ (dialog?.querySelector('[aria-current="true"]'))?.focus({ preventScroll: true });
  }

  closeSinks() {
    /** @type {HTMLDialogElement | undefined} */ (this.refs.sinkDialog)?.close();
  }

  /** @param {Event} event */
  onSinkDialogClick(event) {
    if (event.composedPath()[0] === this.refs.sinkDialog) this.closeSinks();
  }

  /** Bowl filter chips in the sink picker. */
  /** @param {Event} event */
  filterSinks(event) {
    const bowls = /** @type {HTMLElement} */ (event.target).dataset.bowls ?? '';
    const dialog = /** @type {HTMLElement | undefined} */ (this.refs.sinkDialog);
    if (!dialog) return;
    for (const chip of dialog.querySelectorAll('.tc__chip')) {
      chip.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (chip).dataset.bowls === bowls));
    }
    for (const card of dialog.querySelectorAll('[data-sink-id]')) {
      /** @type {HTMLElement} */ (card).hidden = Boolean(bowls) && /** @type {HTMLElement} */ (card).dataset.bowls !== bowls;
    }
  }

  /** @param {Event} event */
  selectSink(event) {
    const id = Number(/** @type {HTMLElement} */ (event.target).dataset.sinkId);
    this.closeSinks();
    if (!id) return;
    this.#state.sinkId = id;
    this.#state.sinkTouched = true;
    this.#render('sink');
  }

  openWastes() {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.wasteDialog);
    dialog?.showModal();
    // Always open at the top of the grid; keep keyboard focus on the current choice without scrolling to it.
    dialog?.scrollTo(0, 0);
    /** @type {HTMLElement | null | undefined} */ (dialog?.querySelector('[aria-current="true"]'))?.focus({ preventScroll: true });
  }

  closeWastes() {
    /** @type {HTMLDialogElement | undefined} */ (this.refs.wasteDialog)?.close();
  }

  /** @param {Event} event */
  onWasteDialogClick(event) {
    if (event.composedPath()[0] === this.refs.wasteDialog) this.closeWastes();
  }

  /** @param {Event} event */
  selectWaste(event) {
    this.#state.wasteFinish = /** @type {HTMLElement} */ (event.target).dataset.waste ?? '';
    this.closeWastes();
    this.#render('waste');
  }

  /** @param {Event} event */
  setWarranty(event) {
    this.#state.warranty = /** @type {HTMLInputElement} */ (event.target).value === '1';
    this.#render('warranty');
  }

  openSinkSpec() {
    const sink = this.#sink;
    if (sink) this.#showSpec(sink, this.#shortTitle(sink).name, () => this.#sink === sink);
  }

  /** @param {Event} event */
  selectThumb(event) {
    this.#state.galleryIndex = Number(/** @type {HTMLElement} */ (event.target).dataset.index) || 0;
    this.#renderGallery();
  }

  /** Opens the selected tap's description (its spec) in the info pop-out. Fetched on demand, then cached. */
  openSpec() {
    const tap = this.#tap;
    if (tap) this.#showSpec(tap, `${this.#rangeName(tap)} · ${this.#finishLabel(tap)}`, () => this.#tap === tap);
  }

  /**
   * Shows a product's description in the info pop-out. Fetched on demand, then cached.
   *
   * @param {TapProduct} product
   * @param {string} title
   * @param {() => boolean} stillSelected Bail out if the selection changed while loading.
   */
  async #showSpec(product, title, stillSelected) {
    const { infoTitle, infoBody } = this.refs;
    if (!infoBody) return;

    if (infoTitle) infoTitle.textContent = title;
    this.#openInfo();

    const link = document.createElement('a');
    link.className = 'tc__info-link';
    link.href = `/products/${product.handle}`;
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = this.#data.specLinkLabel || 'View full product page';

    if (!this.#specCache.has(product.handle)) {
      infoBody.textContent = 'Loading…';
      try {
        const response = await fetch(`/products/${encodeURIComponent(product.handle)}.js`);
        if (!response.ok) throw new Error(String(response.status));
        const json = await response.json();
        this.#specCache.set(product.handle, json.description || '');
      } catch {
        infoBody.replaceChildren(link);
        return;
      }
    }

    if (!stillSelected()) return;
    infoBody.innerHTML = this.#specCache.get(product.handle) || '';
    infoBody.append(link);
  }

  openCompare() {
    const { infoTitle, infoBody, boilerCompare } = this.refs;
    if (!(boilerCompare instanceof HTMLTemplateElement) || !infoBody) return;

    if (infoTitle) infoTitle.textContent = this.#data.compareTitle || '';
    infoBody.replaceChildren(boilerCompare.content.cloneNode(true));
    this.#openInfo();
  }

  closeInfo() {
    /** @type {HTMLDialogElement | undefined} */ (this.refs.infoDialog)?.close();
  }

  /** @param {Event} event */
  onInfoDialogClick(event) {
    if (event.composedPath()[0] === this.refs.infoDialog) this.closeInfo();
  }

  #openInfo() {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.infoDialog);
    if (dialog && !dialog.open) dialog.showModal();
    dialog?.scrollTo(0, 0);
  }

  scrollToSummary() {
    this.refs.summary?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async submit() {
    const button = /** @type {HTMLButtonElement | undefined} */ (this.refs.submit);
    const error = /** @type {HTMLElement | undefined} */ (this.refs.error);
    const lines = this.#lines;
    if (!button || !lines.length) return;

    if (lines.some((line) => !line.product.available)) {
      this.#showError('One of your selections is out of stock. Please choose another option.');
      return;
    }

    button.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    const items = lines.map((line) => ({
      id: line.product.variantId,
      quantity: line.qty,
      properties: { _source: this.#data.source || 'configurator' },
    }));

    try {
      // @ts-ignore Theme is a global set in snippets/scripts.liquid
      const url = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
      const response = await fetch(url, fetchConfig('json', { body: JSON.stringify({ items }) }));
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Something went wrong. Please try again.');
      }

      // Shopify's shareable discount link stores the code on the session, then forwards to checkout with it applied.
      // Two codes ("A,B") go on the cart instead, which accepts a comma-separated list.
      const code = this.#data.discountCode;
      if (code?.includes(',')) {
        const update = await fetch('/cart/update.js', fetchConfig('json', { body: JSON.stringify({ discount: code }) }));
        if (!update.ok) throw new Error('Something went wrong. Please try again.');
        window.location.href = '/checkout';
      } else {
        window.location.href = code ? `/discount/${encodeURIComponent(code)}?redirect=%2Fcheckout` : '/checkout';
      }
    } catch (err) {
      button.removeAttribute('aria-busy');
      this.#showError(err instanceof Error ? err.message : String(err));
    }
  }

  /** @param {string} message */
  #showError(message) {
    const error = /** @type {HTMLElement | undefined} */ (this.refs.error);
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  /* ---------- Rendering ---------- */

  /**
   * @param {'tap' | 'boiler' | 'filter'} [focus] Which line the gallery should jump to.
   */
  #render(focus = 'tap') {
    const range = this.#range;
    const base = this.#base;
    if (!base) return;

    // Drop selections that are no longer valid.
    if (!this.#twin?.available) this.#state.upgraded = false;
    if (!this.#boilerEligible) this.#state.boiler = 'standard';
    if (this.#data.sink && (!this.#state.sinkTouched || !this.#sink)) {
      this.#state.sinkId = this.#defaultSink()?.id ?? 0;
    }
    if (this.#state.wasteFinish && !this.#waste?.available) this.#state.wasteFinish = '';

    this.#renderRange(range, base);
    this.#renderSwatches(range, base);
    this.#renderUpgrade(range, base);
    this.#renderBoiler();
    this.#renderSink();
    this.#renderWaste();
    this.#renderSummary();
    this.#numberSteps();

    if (focus !== 'tap') {
      const media = this.#galleryMedia();
      const index = media.findIndex((item) => item.focus === focus);
      if (index >= 0) this.#state.galleryIndex = index;
    }
    this.#renderGallery();
    this.#syncUrl();
  }

  /**
   * @param {TapRange} range
   * @param {TapProduct} base
   */
  #renderRange(range, base) {
    const { rangeTitle, rangeDesc, rangeImage } = this.refs;
    if (rangeTitle) rangeTitle.textContent = range.title;
    if (rangeDesc) rangeDesc.textContent = [range.system, range.description].filter(Boolean).join(' · ');
    if (rangeImage instanceof HTMLImageElement) {
      rangeImage.src = base.images[0]?.thumb ?? '';
      rangeImage.alt = range.title;
    }

    for (const card of this.querySelectorAll('.tc__range-card')) {
      card.setAttribute('aria-current', String(/** @type {HTMLElement} */ (card).dataset.rangeId === range.id));
    }
  }

  /**
   * @param {TapRange} range
   * @param {TapProduct} base
   */
  #renderSwatches(range, base) {
    const { swatches, finishName, finishDelta } = this.refs;
    const products = this.#rangeProducts(range);
    const prices = products.filter((product) => product.available).map((product) => product.price);
    const minPrice = prices.length ? Math.min(...prices) : base.price;

    if (swatches) {
      swatches.replaceChildren(
        ...products.map((product) => {
          const button = document.createElement('button');
          const label = this.#finishLabel(product);
          button.type = 'button';
          button.className = 'tc__swatch';
          button.dataset.productId = String(product.id);
          button.setAttribute('role', 'radio');
          button.setAttribute('aria-checked', String(product.id === base.id));
          button.setAttribute('aria-label', product.available ? label : `${label} (sold out)`);
          button.setAttribute('title', label);
          button.setAttribute('on:click', '/selectFinish');
          button.disabled = !product.available;

          const dot = document.createElement('span');
          dot.style.setProperty('--swatch', this.#swatchFor(product));
          button.append(dot);
          return button;
        })
      );
    }

    if (finishName) finishName.textContent = this.#finishLabel(base);
    if (finishDelta) {
      const delta = base.price - minPrice;
      finishDelta.textContent = delta > 0 ? `+ ${this.#money(delta)}` : this.#money(this.#displayPrice(base));
    }
  }

  /** @param {TapProduct} product */
  #swatchFor(product) {
    const key = normaliseFinish(product.colour) || normaliseFinish(this.#finishLabel(product));
    const custom = Object.entries(this.#data.swatches ?? {}).find(([name]) => normaliseFinish(name) === key)?.[1];

    if (custom?.image) return `url("${custom.image}")`;
    if (custom?.colour) return custom.colour;
    if (DEFAULT_SWATCHES[key]) return DEFAULT_SWATCHES[key];

    const thumb = product.images[0]?.thumb;
    return thumb ? `url("${thumb}")` : '#ccc';
  }

  /**
   * @param {TapRange} range
   * @param {TapProduct} base
   */
  #renderUpgrade(range, base) {
    const card = /** @type {HTMLElement | null} */ (this.querySelector('[data-step="upgrade"]'));
    if (!card) return;

    const twin = this.#twin;
    card.hidden = !twin;
    // The spec button lives on the upgrade step; when that step is hidden, show the copy on step 01 instead.
    if (this.refs.specFallback) /** @type {HTMLElement} */ (this.refs.specFallback).hidden = Boolean(twin);
    if (!twin) return;

    const { baseSystemTitle, baseSystemImage, upgradeTitle, upgradeText, upgradeDelta, upgradeImage } = this.refs;
    if (baseSystemTitle) baseSystemTitle.textContent = [range.system, range.title].filter(Boolean).join(' · ');
    if (upgradeTitle) upgradeTitle.textContent = range.upgrade.title || this.#rangeName(twin);
    if (upgradeText) upgradeText.textContent = range.upgrade.text;
    if (upgradeDelta) {
      upgradeDelta.textContent = twin.available
        ? `+ ${this.#money(Math.max(twin.price - base.price, 0))}`
        : 'Sold out';
    }
    if (baseSystemImage instanceof HTMLImageElement) baseSystemImage.src = base.images[0]?.thumb ?? '';
    if (upgradeImage instanceof HTMLImageElement) upgradeImage.src = twin.images[0]?.thumb ?? '';

    for (const input of card.querySelectorAll('input[type="radio"]')) {
      const radio = /** @type {HTMLInputElement} */ (input);
      radio.checked = radio.value === (this.#state.upgraded ? '1' : '0');
      if (radio.value === '1') radio.disabled = !twin.available;
    }
  }

  #renderSink() {
    const sink = this.#sink;
    const { sinkTitle, sinkDesc, sinkImage, sinkPrice, sinkDialog } = this.refs;
    if (!sink) return;

    const { name, spec } = this.#shortTitle(sink);
    if (sinkTitle) sinkTitle.textContent = name;
    if (sinkDesc) sinkDesc.textContent = [this.#bowlsLabel(sink), spec].filter(Boolean).join(' · ');
    if (sinkImage instanceof HTMLImageElement) {
      sinkImage.src = sink.images[0]?.thumb ?? '';
      sinkImage.alt = name;
    }
    if (sinkPrice) {
      sinkPrice.replaceChildren();
      const now = this.#displayPrice(sink);
      if (this.#wasPrice(sink) > now) {
        const was = document.createElement('s');
        was.textContent = this.#money(this.#wasPrice(sink));
        sinkPrice.append(was);
      }
      sinkPrice.append(`+ ${this.#money(now)}`);
    }
    for (const card of sinkDialog?.querySelectorAll('[data-sink-id]') ?? []) {
      card.setAttribute('aria-current', String(Number(/** @type {HTMLElement} */ (card).dataset.sinkId) === sink.id));
    }
  }

  /**
   * Waste options depend on the sink (overflow type, quantity) and the tap (matching finish), so the picker cards and
   * the "current choice" card are built here. Same pattern as the sink step: a summary card that opens a grid.
   */
  #renderWaste() {
    const { wasteGrid, wasteCurrent, wasteTitle, wasteDesc, wasteImage, wastePrice } = this.refs;
    const sink = this.#sink;
    if (!wasteGrid || !this.#data.waste) return;

    const card = /** @type {HTMLElement | null} */ (wasteGrid.closest('[data-step]'));
    if (card) card.hidden = !sink;
    if (!sink) return;

    const qty = this.#wasteQty;
    const plural = qty > 1 ? 's' : '';
    const supplied = this.#suppliedWasteFinish(sink);
    const keepTitle = supplied
      ? `Keep the ${supplied} waste${plural} supplied with your sink`
      : this.#data.waste.keepLabel || 'Keep the waste supplied with your sink';
    const detail = [
      this.#sinkHasOverflow ? 'With overflow plates to suit your sink' : '',
      qty > 1 ? `× ${qty}, ${(this.#data.waste?.perBowlLabel || 'One per bowl').toLowerCase()}` : '',
    ]
      .filter(Boolean)
      .join(' · ');

    // If the supplied waste already matches the tap, only "keep" gets the badge; otherwise badge the matching upgrade.
    const suppliedMatches = this.#matchesTap(supplied);
    const isMatch = (/** @type {string} */ finish) => !suppliedMatches && this.#matchesTap(finish);
    const options = this.#wasteOptions;
    options.sort((a, b) => Number(isMatch(b.finish)) - Number(isMatch(a.finish)));

    /** @param {TapProduct | undefined} product */
    const priceNodes = (product) => {
      if (!product) return [this.#data.includedLabel || 'Included'];
      if (!product.available) return ['Sold out'];
      const nodes = [];
      const now = this.#displayPrice(product, qty);
      if (this.#wasPrice(product) * qty > now) {
        const was = document.createElement('s');
        was.textContent = this.#money(this.#wasPrice(product) * qty);
        nodes.push(was, ' ');
      }
      nodes.push(`+ ${this.#money(now)}`);
      return nodes;
    };

    /**
     * @param {string} value
     * @param {string} title
     * @param {string} text
     * @param {TapProduct | undefined} product
     * @param {boolean} match
     * @param {string | undefined} image
     */
    const tile = (value, title, text, product, match, image) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tc__range-card';
      button.dataset.waste = value;
      button.setAttribute('on:click', '/selectWaste');
      button.setAttribute('aria-current', String(this.#state.wasteFinish === value));
      button.disabled = Boolean(product && !product.available);

      if (match) {
        const badge = document.createElement('span');
        badge.className = 'tc__range-badge';
        badge.textContent = this.#data.waste?.matchLabel || 'Matches your tap';
        button.append(badge);
      }
      const media = document.createElement('span');
      media.className = 'tc__range-card-media';
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 200;
        img.height = 200;
        media.append(img);
      }
      const name = document.createElement('span');
      name.className = 'tc__range-card-title';
      name.textContent = title;
      button.append(media, name);
      if (text) {
        const desc = document.createElement('span');
        desc.className = 'tc__range-card-desc';
        desc.textContent = text;
        button.append(desc);
      }
      const price = document.createElement('span');
      price.className = 'tc__range-card-price';
      price.append(...priceNodes(product));
      button.append(price);
      return button;
    };

    wasteGrid.replaceChildren(
      // The supplied waste has no product of its own; show the sink so the customer sees what they're keeping.
      tile('', keepTitle, '', undefined, suppliedMatches, sink.images[0]?.thumb),
      ...options.map(({ finish, product }) =>
        tile(finish, `${finish} strainer waste${plural}`, detail, product, isMatch(finish), product.images[0]?.thumb)
      )
    );

    // Summary card for the current choice
    const chosen = this.#waste;
    const chosenFinish = chosen ? this.#state.wasteFinish : '';
    if (wasteTitle) wasteTitle.textContent = chosen ? `${chosenFinish} strainer waste${plural}` : keepTitle;
    if (wasteDesc) {
      const match = chosen ? isMatch(chosenFinish) : suppliedMatches;
      wasteDesc.textContent = [chosen ? detail : '', match ? this.#data.waste?.matchLabel || 'Matches your tap' : '']
        .filter(Boolean)
        .join(' · ');
    }
    if (wasteImage instanceof HTMLImageElement) {
      const src = chosen?.images[0]?.thumb;
      wasteImage.hidden = !src;
      if (src) wasteImage.src = src;
    }
    if (wastePrice) wastePrice.replaceChildren(...priceNodes(chosen));
    wasteCurrent?.setAttribute('aria-label', wasteTitle?.textContent || '');
  }

  #renderBoiler() {
    const card = /** @type {HTMLElement | null} */ (this.querySelector('[data-step="boiler"]'));
    if (!card) return;

    card.hidden = !this.#boilerEligible;
    for (const input of card.querySelectorAll('input[type="radio"]')) {
      const radio = /** @type {HTMLInputElement} */ (input);
      radio.checked = radio.value === this.#state.boiler;
    }
  }

  #renderSummary() {
    const { lines: list, total, barTotal, barText } = this.refs;
    const lines = this.#lines;
    const totalCents = this.#total;

    if (list) {
      list.replaceChildren(
        ...lines.map((line) => {
          const li = document.createElement('li');
          li.className = 'tc__line';

          const name = document.createElement('span');
          name.textContent = line.name;
          if (line.sub) {
            const sub = document.createElement('small');
            sub.textContent = line.sub;
            name.append(sub);
          }

          // Strike through the compare-at price (or the normal price when only the page discount applies).
          const wasPrice = this.#wasPrice(line.product) * line.qty;
          const nowPrice = line.product.price * line.qty - this.#lineDiscount(line);
          const price = document.createElement('strong');
          if (wasPrice > nowPrice) {
            const was = document.createElement('s');
            was.textContent = this.#money(wasPrice);
            price.append(was);
          }
          price.append(this.#money(nowPrice));

          li.append(name, price);
          return li;
        })
      );

      // Percentage discount: one order-level line, exactly as checkout shows it.
      const orderOff = this.#orderDiscount(lines);
      if (orderOff > 0) {
        const li = document.createElement('li');
        li.className = 'tc__line tc__line--discount';
        const name = document.createElement('span');
        name.textContent = `${this.#data.discountPercent}% off your bundle`;
        const price = document.createElement('strong');
        price.textContent = `-${this.#money(orderOff)}`;
        li.append(name, price);
        list.append(li);
      }
    }

    const orderDiscount = this.#orderDiscount(lines);
    const { discountNote, savings, savingsAmount } = this.refs;
    if (discountNote) discountNote.hidden = !(orderDiscount > 0 || lines.some((line) => this.#lineDiscount(line) > 0));

    const saved =
      lines.reduce(
        (sum, line) =>
          sum + (this.#wasPrice(line.product) - line.product.price) * line.qty + this.#lineDiscount(line),
        0
      ) + orderDiscount;
    if (savings) savings.hidden = saved <= 0;
    if (savingsAmount) savingsAmount.textContent = this.#money(saved);

    const formatted = this.#money(totalCents);
    if (total) total.textContent = formatted;
    if (barTotal) barTotal.textContent = formatted;
    if (barText) {
      const tap = lines[0];
      barText.textContent = lines
        .map((line, index) => (index === 0 && tap ? `${tap.name} · ${tap.sub}` : line.name))
        .join(' · ');
    }

    this.#syncKlarna();
  }

  /**
   * Klarna On-Site Messaging app block: feed it the configured total (in minor units) and ask it to re-render.
   * Returns early when the placements already show the total, so the attribute observer can't loop.
   */
  #syncKlarna() {
    const { klarna } = this.refs;
    if (!klarna) return;

    const amount = String(this.#total);
    let changed = false;
    for (const placement of klarna.querySelectorAll('klarna-placement')) {
      if (placement.getAttribute('data-purchase-amount') === amount) continue;
      placement.setAttribute('data-purchase-amount', amount);
      changed = true;
    }
    if (!changed) return;

    // @ts-ignore Klarna globals come from the app block
    window.Klarna?.OnsiteMessaging?.refresh?.();
    // @ts-ignore Legacy Klarna loader
    window.KlarnaOnsiteService?.push?.({ eventName: 'refresh-placements' });
  }

  /** The Klarna app script loads after us and resets the amount to the (empty) page product price; put it back. */
  #observeKlarna() {
    const { klarna } = this.refs;
    if (!klarna) return;

    this.#klarnaObserver?.disconnect();
    this.#klarnaObserver = new MutationObserver(() => this.#syncKlarna());
    this.#klarnaObserver.observe(klarna, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-purchase-amount'],
    });
  }

  #numberSteps() {
    let index = 0;
    for (const card of this.querySelectorAll('[data-step]')) {
      const label = card.querySelector('[data-step-number]');
      if (!label || /** @type {HTMLElement} */ (card).hidden) continue;
      index += 1;
      label.textContent = `Step ${String(index).padStart(2, '0')}`;
    }
  }

  /** Images of the selected tap, followed by the selected add-ons. */
  #galleryMedia() {
    /** @type {(TapImage & { focus: string })[]} */
    const media = [];
    for (const line of this.#lines) {
      const images = line.focus === 'tap' ? line.product.images : line.product.images.slice(0, 1);
      for (const image of images) media.push({ ...image, focus: line.focus });
    }
    return media;
  }

  #renderGallery() {
    const { stageImage, thumbs } = this.refs;
    const media = this.#galleryMedia();
    const index = Math.min(this.#state.galleryIndex, Math.max(media.length - 1, 0));
    const current = media[index];

    if (stageImage instanceof HTMLImageElement && current && stageImage.getAttribute('src') !== current.src) {
      stageImage.removeAttribute('srcset');
      stageImage.removeAttribute('sizes');
      stageImage.classList.add('is-loading');
      stageImage.onload = () => stageImage.classList.remove('is-loading');
      stageImage.src = current.src;
      stageImage.alt = current.alt;
    }

    if (thumbs) {
      thumbs.hidden = media.length < 2;
      thumbs.replaceChildren(
        ...media.map((image, i) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'tc__thumb';
          button.dataset.index = String(i);
          button.setAttribute('role', 'listitem');
          button.setAttribute('aria-label', image.alt);
          button.setAttribute('aria-current', String(i === index));
          button.setAttribute('on:click', '/selectThumb');

          const img = document.createElement('img');
          img.src = image.thumb;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 64;
          img.height = 64;
          button.append(img);
          return button;
        })
      );
    }
  }

  #syncUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('range', this.#range.handle);
    if (this.#base) url.searchParams.set('finish', this.#base.handle);
    if (this.#state.upgraded) url.searchParams.set('upgrade', '1');
    else url.searchParams.delete('upgrade');
    if (this.#sink && this.#state.sinkTouched) url.searchParams.set('sink', this.#sink.handle);
    else url.searchParams.delete('sink');
    window.history.replaceState(window.history.state, '', url);
  }

  /** Shows the sticky total bar while the configurator is on screen but the summary card is not. */
  #observeBar() {
    const { bar, summary } = this.refs;
    if (!bar || !summary) return;

    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === summary) this.#summaryVisible = entry.isIntersecting;
        if (entry.target === this) this.#sectionVisible = entry.isIntersecting;
      }
      const visible = this.#sectionVisible && !this.#summaryVisible;
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', String(!visible));
      const button = bar.querySelector('button');
      if (button) button.tabIndex = visible ? 0 : -1;
    });
    this.#observer.observe(summary);
    this.#observer.observe(this);
  }
}

if (!customElements.get('tap-configurator')) {
  customElements.define('tap-configurator', TapConfigurator);
}
